import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Save, Loader2, User, Settings as SettingsIcon, Building2, Link2, Shield, Briefcase, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STAFF_TYPE_LABELS = {
  'classroom_teacher': 'Classroom Teacher',
  'school_administrator': 'School Administrator',
  'counselor': 'Counselor',
  'instructional_coach': 'Instructional Coach',
  'speech_language_pathologist': 'Speech-Language Pathologist',
  'reading_specialist': 'Reading Specialist',
  'math_specialist': 'Math Specialist',
  'behavior_specialist': 'Behavior Specialist',
  'intervention_support_staff': 'Intervention Support Staff',
  'school_psychologist': 'School Psychologist',
  'other': 'Other'
};

const ROLE_LABELS = {
  'super_admin': 'Super Admin',
  'admin': 'School Admin',
  'specialist': 'Interventionist',
  'viewer': 'Viewer'
};

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState({
    app_name: 'MTSS Progress Tracker'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, settingsRes] = await Promise.all([
        apiService.getUserProfile(),
        apiService.getSettings()
      ]);
      setProfile(profileRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.updateSettings({ app_name: settings.app_name });
      toast.success('Settings saved');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  const getStaffTypeLabel = (type, otherValue) => {
    // If type is "other" and we have a custom value, show it
    if (type === 'other' && otherValue) {
      return otherValue;
    }
    return STAFF_TYPE_LABELS[type] || type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not specified';
  };

  const getRoleLabel = (role) => {
    return ROLE_LABELS[role] || role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Viewer';
  };

  if (loading) {
    return (
      <div className="page-container" data-testid="settings-page">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="settings-page">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">View your account information and manage preferences</p>
      </div>

      <div className="grid gap-6" style={{ maxWidth: '700px' }}>
        {/* User Profile - Structured Account Info */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2">
              <User className="h-4 w-4" />
              Account Information
            </h3>
          </div>
          <div className="card-content">
            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-[#E5E5E5]">
              <div className="user-avatar" style={{ width: 56, height: 56, fontSize: 22 }}>
                {profile?.name?.charAt(0) || user?.name?.charAt(0) || 'U'}
              </div>
              <div>
                <div className="font-semibold text-lg text-[#1C1C1C]">{profile?.name || user?.name}</div>
                <div className="text-sm text-[#595959]">{profile?.email || user?.email}</div>
              </div>
            </div>
            
            {/* Account Details Grid */}
            <div className="space-y-4">
              {/* App Role */}
              <div className="flex items-start gap-3 p-3 bg-[#F3F4F1] rounded-lg">
                <Shield className="h-5 w-5 text-[#2C4C3B] mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-[#595959] uppercase tracking-wide">App Role</div>
                  <div className="text-sm font-medium text-[#1C1C1C] mt-0.5">
                    {getRoleLabel(profile?.role)}
                  </div>
                  <div className="text-xs text-[#A3A3A3] mt-1">
                    {profile?.role === 'super_admin' && 'Full access to all schools and features'}
                    {profile?.role === 'admin' && 'Full access to your school\'s data'}
                    {profile?.role === 'specialist' && 'Can add/edit students, progress, and exports'}
                    {profile?.role === 'viewer' && 'View-only access'}
                  </div>
                </div>
              </div>

              {/* Assigned School */}
              <div className="flex items-start gap-3 p-3 bg-[#F3F4F1] rounded-lg">
                <Building2 className="h-5 w-5 text-[#2C4C3B] mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-[#595959] uppercase tracking-wide">Assigned School</div>
                  <div className="text-sm font-medium text-[#1C1C1C] mt-0.5">
                    {profile?.role === 'super_admin' ? (
                      <span className="text-[#595959] italic">All Schools</span>
                    ) : (
                      profile?.school?.name || 'No school assigned'
                    )}
                  </div>
                  {profile?.school?.code && profile?.role !== 'super_admin' && (
                    <div className="text-xs text-[#A3A3A3] mt-0.5">Code: {profile.school.code}</div>
                  )}
                </div>
              </div>

              {/* Staff Contact Linkage */}
              <div className="flex items-start gap-3 p-3 bg-[#F3F4F1] rounded-lg">
                <Link2 className="h-5 w-5 text-[#2C4C3B] mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-[#595959] uppercase tracking-wide">Linked Staff Contact</div>
                  {profile?.linked_staff_contact ? (
                    <div className="mt-0.5">
                      <div className="text-sm font-medium text-[#1C1C1C]">
                        {profile.linked_staff_contact.display_name}
                      </div>
                      <div className="text-xs text-[#595959]">{profile.linked_staff_contact.email}</div>
                    </div>
                  ) : (
                    <div className="text-sm text-[#A3A3A3] mt-0.5 italic">
                      Not linked to a staff contact
                    </div>
                  )}
                </div>
              </div>

              {/* Staff Type */}
              <div className="flex items-start gap-3 p-3 bg-[#F3F4F1] rounded-lg">
                <Briefcase className="h-5 w-5 text-[#2C4C3B] mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-[#595959] uppercase tracking-wide">Staff Type</div>
                  <div className="text-sm font-medium text-[#1C1C1C] mt-0.5">
                    {profile?.staff_type ? getStaffTypeLabel(profile.staff_type, profile.staff_type_other || profile.linked_staff_contact?.staff_type_other) : (
                      <span className="text-[#A3A3A3] italic">Not specified</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Provider Status */}
              <div className="flex items-start gap-3 p-3 bg-[#F3F4F1] rounded-lg">
                {profile?.can_provide_interventions ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-[#A3A3A3] mt-0.5" />
                )}
                <div className="flex-1">
                  <div className="text-xs font-medium text-[#595959] uppercase tracking-wide">Provider Status</div>
                  <div className="text-sm font-medium mt-0.5">
                    {profile?.can_provide_interventions ? (
                      <span className="text-green-700">Can provide interventions</span>
                    ) : (
                      <span className="text-[#A3A3A3]">Not marked as provider</span>
                    )}
                  </div>
                  <div className="text-xs text-[#A3A3A3] mt-1">
                    {profile?.can_provide_interventions 
                      ? 'You can be assigned to interventions and enter progress data.'
                      : 'You cannot be assigned as an intervention provider.'}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-[#E5E5E5]">
              <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
                Sign Out
              </Button>
            </div>
          </div>
        </div>

        {/* App Settings */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2">
              <SettingsIcon className="h-4 w-4" />
              App Preferences
            </h3>
          </div>
          <div className="card-content">
            <div className="space-y-4">
              <div className="form-group">
                <Label htmlFor="app_name">App Name</Label>
                <Input
                  id="app_name"
                  value={settings.app_name || ''}
                  onChange={(e) => setSettings({ ...settings, app_name: e.target.value })}
                  placeholder="MTSS Progress Tracker"
                  data-testid="app-name-input"
                />
                <p className="text-xs text-[#595959] mt-1">
                  Customize the app name displayed in the sidebar
                </p>
              </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#2C4C3B] hover:bg-[#1E3629]"
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* About */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">About</h3>
          </div>
          <div className="card-content">
            <p className="text-sm text-[#595959] mb-2">
              <strong>MTSS Progress Tracker</strong> is a tool for school-based specialists 
              and intervention teams to track student progress within RTI/MTSS frameworks.
            </p>
            <p className="text-xs text-[#A3A3A3]">
              Version 1.0.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
